import * as React from 'react';
import './pdf.css';
import { observer, inject } from 'mobx-react';
import { BloodworkEntryStore } from '../stores/bloodwork-entry-store';
import { FurtherConsiderationsEntryStore } from '../stores/further-considerations-entry-store';
import { UserProfileUIStore } from '../stores/user-profile-ui-store';

import { Bloodwork } from '../models/Bloodwork';
import { Button, Card, Image } from 'react-bootstrap';

import { BloodworkAnalysis } from '../models/BloodworkAnalysis';
import { PanelField } from '../models/PanelField';
import Moment from 'react-moment';
import moment from 'moment';
import { FurtherConsiderations, PanelMarker, Section } from '../models';
import { DisclaimerSection } from '../components/bloodworkz/disclaimer-section';
import { PDFSection } from '../components/bloodworkz/pdf-section';
import Storage from '@aws-amplify/storage';

const logo = require('../assets/imgs/logo.png');
const html2pdf = require('html2pdf.js');

Storage.configure({
  AWSS3: {
    bucket: 'labsmarts-next',
    region: 'us-east-1',
  },
});

interface BloodworkAnalysisPDFContainerProps {
  bloodworkEntryStore?: BloodworkEntryStore;
  furtherConsiderationsEntryStore?: FurtherConsiderationsEntryStore;
  userProfileUIStore?: UserProfileUIStore;
}

type State = {
  withNotes: boolean;
  bloodworkAnalysis: BloodworkAnalysis;
  previousBloodwork?: Bloodwork;
  furtherConsiderations: FurtherConsiderations;
  headerLogoDataUrl: string | null;
  footerBrandDataUrl: string | null;
  isPdfReady: boolean;
  preparedPdf: any | null; // Pre-generated PDF object ready for save
};

@inject(
  'bloodworkEntryStore',
  'furtherConsiderationsEntryStore',
  'userProfileUIStore',
)
@observer
export class BloodworkAnalysisPDFContainer extends React.Component<
  BloodworkAnalysisPDFContainerProps,
  State
> {
  state: State = {
    withNotes: false,
    bloodworkAnalysis: new BloodworkAnalysis(),
    previousBloodwork: undefined,
    furtherConsiderations: new FurtherConsiderations(),
    headerLogoDataUrl: null,
    footerBrandDataUrl: null,
    isPdfReady: false,
    preparedPdf: null,
  };

  // --- Helpers ---------------------------------------------------------------

  // Turn any URL into a data URL (so html2canvas can render it reliably)
  private toDataUrl = async (url: string): Promise<string> => {
    const res = await fetch(url, { credentials: 'omit' });
    const blob = await res.blob();
    return await new Promise<string>(resolve => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.readAsDataURL(blob);
    });
  };

  // Resolve the HEADER logo (custom if present; otherwise bundled default) to a data URL
  private resolveHeaderLogoDataUrl = async (): Promise<string> => {
    const raw = this.getLogoUrl() as any;
    const candidate = (raw?.default ?? raw) as string | undefined;

    if (candidate === logo) {
      return await this.toDataUrl(candidate);
    }

    // 1) If getLogoUrl() returns http(s), try directly
    if (candidate && /^https?:\/\//i.test(candidate)) {
      try {
        return await this.toDataUrl(candidate);
      } catch {
        /* fall through */
      }
    }

    // 2) Try S3: user-logos/<filename>
    try {
      const key = (candidate || '').toString().split('/').pop();
      if (key) {
        const s3Url = await Storage.get(`user-logos/${key}`);
        return await this.toDataUrl(s3Url as string);
      }
    } catch {
      /* fall through */
    }

    // 3) Fallback: bundled default
    const bundled =
      (require('../assets/imgs/logo.png') as any)?.default ||
      (require('../assets/imgs/logo.png') as any);
    return await this.toDataUrl(bundled);
  };

  // Resolve the FOOTER brand mark (usually the bundled logo) to a data URL
  private resolveFooterBrandDataUrl = async (): Promise<string> => {
    const brandUrl =
      (require('../assets/imgs/logo.png') as any)?.default ||
      (require('../assets/imgs/logo.png') as any);
    return await this.toDataUrl(brandUrl);
  };

  // Prepare logos and PDF data in advance
  private preparePdfAssets = async (): Promise<void> => {
    try {
      // Load logos in parallel
      const [headerLogoDataUrl, footerBrandDataUrl] = await Promise.all([
        this.resolveHeaderLogoDataUrl(),
        this.resolveFooterBrandDataUrl(),
      ]);

      this.setState({
        headerLogoDataUrl,
        footerBrandDataUrl,
        isPdfReady: true,
      });

      // Update the logo in DOM immediately for better UX
      const logoElement = document.getElementById('logo') as HTMLImageElement;
      if (logoElement && headerLogoDataUrl) {
        logoElement.src = headerLogoDataUrl;
      }

      // Now prepare the actual PDF
      await this.preparePdf();
    } catch (error) {
      console.error('Error preparing PDF assets:', error);
      // Set ready to true anyway so export can still work with fallback
      this.setState({ isPdfReady: true });
      this.showOrHidePdfExportButton(true);
    }
  };

  // Prepare the actual PDF object in advance
  private preparePdf = async (): Promise<void> => {
    try {
      const { bloodworkAnalysis, headerLogoDataUrl, footerBrandDataUrl } =
        this.state;

      if (!bloodworkAnalysis.bloodwork) {
        console.error('Bloodwork analysis not ready');
        return;
      }

      const firstInitialOfFirstName =
        bloodworkAnalysis.bloodwork.client.firstName.charAt(0);
      const lastName = bloodworkAnalysis.bloodwork.client.lastName;
      const collectionDate = moment(
        bloodworkAnalysis.bloodwork.collectionDate,
      ).format('YYYY-MM-DD');
      const filename = `${lastName}_${firstInitialOfFirstName}-LabSmarts_Analysis_${collectionDate}.pdf`;

      const rootEl = document.getElementById('test-results-analysis-panel');
      const pdfExportButton = document.getElementById('pdf-export-button');
      const cardHeaders = document.getElementsByClassName('card-header');

      if (pdfExportButton) pdfExportButton.style.opacity = '0';

      if (cardHeaders) {
        for (let i = 0; i < cardHeaders.length; i++) {
          (cardHeaders[i] as HTMLElement).style.marginTop = '-1px';
        }
      }

      if (!rootEl) {
        console.error('PDF root element not found');
        return;
      }

      // Helper: detect 'PNG' or 'JPEG' from data URL
      const detectType = (dataUrl: string): 'PNG' | 'JPEG' => {
        return dataUrl.startsWith('data:image/jpeg') ||
          dataUrl.startsWith('data:image/jpg')
          ? 'JPEG'
          : 'PNG';
      };
      const footerImgType = footerBrandDataUrl
        ? detectType(footerBrandDataUrl)
        : 'PNG';

      const opt = {
        margin: [5, 0, 5, 0],
        filename,
        image: { type: 'jpeg', quality: 1 },
        jsPDF: { orientation: 'p' },
        html2canvas: {
          scale: 1,
          useCORS: true,
          allowTaint: false,
          scrollX: 0,
          scrollY: 0,
          onclone: (doc: Document) => {
            const img = doc.getElementById('logo') as HTMLImageElement | null;
            if (img && headerLogoDataUrl) img.src = headerLogoDataUrl;
          },
        },
      } as const;

      // Generate PDF and add footer
      const preparedPdf = await html2pdf()
        .set(opt)
        .from(rootEl)
        .toPdf()
        .get('pdf')
        .then((pdf: any) => {
          // Only add footer if we have the brand data URL
          if (footerBrandDataUrl) {
            // Compute brand logo aspect ratio from the data URL
            const props = pdf.getImageProperties(footerBrandDataUrl);
            const naturalRatio = props ? props.width / props.height : 5.5; // fallback ratio

            const footerFontSize = 8;
            const logoHeight = 3.8; // tweak to taste
            const logoWidth = logoHeight * naturalRatio;

            const rightText = 'Powered by';
            const leftPadding = 8;
            const rightPadding = 8;
            const bottomPadding = 6;
            const gap = 2;

            const fullName = `${bloodworkAnalysis.bloodwork.client.firstName} ${bloodworkAnalysis.bloodwork.client.lastName}`;
            const formattedDate = moment(
              bloodworkAnalysis.bloodwork.collectionDate,
            ).format('MM/DD/YYYY');
            const leftBase = `Prepared for: ${fullName}  •  Collection Date: ${formattedDate}`;

            const totalPages = pdf.internal.getNumberOfPages();
            for (let i = 1; i <= totalPages; i++) {
              pdf.setPage(i);
              const pageWidth = pdf.internal.pageSize.getWidth();
              const pageHeight = pdf.internal.pageSize.getHeight();
              const baselineY = pageHeight - bottomPadding;

              pdf.setFontSize(footerFontSize);

              // Right block width
              const rightTextWidth = pdf.getTextWidth(rightText);
              const rightBlockWidth = rightTextWidth + gap + logoWidth;
              const rightStartX = pageWidth - rightPadding - rightBlockWidth;

              // Fit left text to avoid overlap
              const maxLeftWidth = rightStartX - leftPadding - 4;
              const fit = (s: string) => {
                if (pdf.getTextWidth(s) <= maxLeftWidth) return s;
                let t = s;
                while (t.length > 4 && pdf.getTextWidth(t + '…') > maxLeftWidth)
                  t = t.slice(0, -1);
                return t + '…';
              };
              const leftText = fit(leftBase);

              // Draw left and right blocks
              pdf.text(leftText, leftPadding, baselineY);
              pdf.text(rightText, rightStartX, baselineY);
              pdf.addImage(
                footerBrandDataUrl,
                footerImgType,
                rightStartX + rightTextWidth + gap,
                baselineY - logoHeight + 0.8,
                logoWidth,
                logoHeight,
              );
            }
          }

          return pdf;
        });

      this.setState({
        preparedPdf,
        isPdfReady: true,
      });

      // Show button only when PDF is ready
      this.showOrHidePdfExportButton(true);
      if (pdfExportButton) pdfExportButton.style.opacity = '1';
    } catch (error) {
      console.error('Error preparing PDF:', error);
      // Set ready to true anyway so export can still work with fallback
      this.setState({ isPdfReady: true });
      this.showOrHidePdfExportButton(true);
    }
  };

  // --------------------------------------------------------------------------

  componentDidMount() {
    this.showOrHidePdfExportButton(false);

    const withNotes = sessionStorage.getItem('exportWithNotes') === 'true';
    const bloodworkJSON = sessionStorage.getItem('bloodwork');
    const previousBloodworkJSON = sessionStorage.getItem('previousBloodwork');
    sessionStorage.clear();

    // Ensure profile is loaded (for custom logo)
    const { userProfileUIStore } = this.props;
    if (userProfileUIStore && !userProfileUIStore.currentUserProfile) {
      userProfileUIStore.loadCurrentUserProfile();
    }

    this.props.bloodworkEntryStore
      ?.loadBloodworkRanges()
      .then(bloodworkRanges => {
        const bloodwork = new Bloodwork(JSON.parse(bloodworkJSON!));
        const previousBloodwork = previousBloodworkJSON
          ? new Bloodwork(JSON.parse(previousBloodworkJSON))
          : undefined;

        const bloodworkAnalysis = new BloodworkAnalysis({
          bloodwork,
          bloodworkRanges,
          bloodworkFields: PanelField.ALL_FIELDS,
        });
        bloodworkAnalysis.init().performAnalysis();

        if (previousBloodwork && previousBloodwork.collectionDate) {
          const previousBloodworkAnalysis = new BloodworkAnalysis({
            bloodwork: previousBloodwork,
            bloodworkRanges,
            bloodworkFields: PanelField.ALL_FIELDS,
          });
          previousBloodworkAnalysis.init();
          bloodworkAnalysis.preparePreviousPanelMarkers(
            previousBloodworkAnalysis,
          );
        }
        return { bloodworkAnalysis, previousBloodwork };
      })
      .then(({ bloodworkAnalysis, previousBloodwork }) => {
        return this.props
          .furtherConsiderationsEntryStore!.loadFurtherConsiderations()
          .then(furtherConsiderations => ({
            bloodworkAnalysis,
            previousBloodwork,
            furtherConsiderations,
          }));
      })
      .then(
        ({ bloodworkAnalysis, previousBloodwork, furtherConsiderations }) => {
          this.setState({
            withNotes,
            bloodworkAnalysis,
            previousBloodwork,
            furtherConsiderations,
          });
        },
      );
  }

  componentDidUpdate(_prevProps: any, prevState: State) {
    if (
      !Object.values(prevState.bloodworkAnalysis).filter(Boolean).length &&
      Object.values(this.state.bloodworkAnalysis).filter(Boolean).length
    ) {
      setTimeout(() => this.preparePdfAssets(), 2000);
    }
  }

  componentWillUnmount() {
    sessionStorage.clear();
  }

  componentWillReceiveProps() {
    this.scrollTop();
  }

  scrollTop() {
    if (
      typeof window !== 'undefined' &&
      typeof window.scrollTo === 'function'
    ) {
      window.scrollTo(0, 0);
    }
  }

  showOrHidePdfExportButton(show: boolean, timeout: number = 0) {
    setTimeout(() => {
      const opacity = show ? '1' : '0';
      const pdfExportButton = document.getElementById('pdf-export-button');
      if (pdfExportButton) {
        pdfExportButton.style.opacity = opacity;
      }
    }, timeout);
  }

  getLogoUrl = (): string => {
    const { userProfileUIStore } = this.props;
    const userProfile = userProfileUIStore?.currentUserProfile;
    return (
      userProfile?.hasCustomLogo() ? userProfile.customLogoUrl : logo
    ) as any;
  };

  // Legacy helper still used elsewhere
  getImageAsDataUrl = async (url: string) => {
    const res = await fetch(url);
    const blob = await res.blob();
    return new Promise(resolve => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.readAsDataURL(blob);
    });
  };

  // ========= EXPORT =========
  export = async () => {
    const { preparedPdf, isPdfReady, bloodworkAnalysis } = this.state;
    const firstInitialOfFirstName =
      bloodworkAnalysis.bloodwork.client.firstName.charAt(0);
    const lastName = bloodworkAnalysis.bloodwork.client.lastName;
    const collectionDate = moment(
      bloodworkAnalysis.bloodwork.collectionDate,
    ).format('YYYY-MM-DD');
    const filename = `${lastName}_${firstInitialOfFirstName}-LabSmarts_Analysis_${collectionDate}.pdf`;

    if (preparedPdf && isPdfReady) {
      preparedPdf.save(filename);
    } else {
      // Fallback: prepare PDF on the fly (shouldn't happen normally)
      console.log('PDF not prepared, falling back to on-demand generation...');
      await this.preparePdf();
      if (this.state.preparedPdf) {
        this.state.preparedPdf.save(filename);
      }
    }
  };

  pageBreakClassName(panelMarkers: PanelMarker[]): string {
    return panelMarkers.length > 0 ? 'html2pdf__page-break' : '';
  }

  render() {
    const { bloodworkAnalysis, furtherConsiderations, isPdfReady } = this.state;
    if (!bloodworkAnalysis.bloodwork) {
      return null;
    }
    const today = new Date().toISOString();

    // Only show Immune System Analysis if WBC has a value
    const wbc = (bloodworkAnalysis as any).wbc;
    const hasWbcValue = wbc?.hasValue?.() === true;

    // Clean leading blank lines/spaces from the global Notes block
    const notesObj = bloodworkAnalysis.bloodwork.bloodworkNotes.notes.find(
      n => n.section === Section.NOTES,
    );
    const globalNotesText = (notesObj?.text ?? '').trimStart();

    return (
      <div className="pdf-page" id="test-results-analysis-panel">
        <div className="text-right" style={{ marginTop: 12 }}>
          <Button
            id="pdf-export-button"
            onClick={this.export}
            disabled={!isPdfReady}
            style={{ opacity: isPdfReady ? 1 : 0 }}
          >
            {isPdfReady ? 'Export to PDF' : 'Preparing PDF...'}
          </Button>
        </div>

        {/* Header */}
        <div className="text-center" style={{ marginTop: 24 }}>
          <Image
            id="logo"
            src={this.getLogoUrl()}
            alt="Current Logo"
            fluid
            style={{
              maxHeight: '100px',
              maxWidth: '400px',
              objectFit: 'contain',
            }}
          />
        </div>
        <div style={{ marginTop: 16 }}>
          <h3 className="text-center">
            <strong>Blood Work Analysis Report</strong>
          </h3>
          <div style={{ height: 12 }} aria-hidden />
        </div>

        {/* First-page meta */}
        <div style={{ marginTop: 48 }}>
          <div className="html2pdf__page-break">
            <h5>
              <strong>Prepared for</strong>
            </h5>
            <h5>
              {bloodworkAnalysis.bloodwork.client.firstName}{' '}
              {bloodworkAnalysis.bloodwork.client.lastName}
            </h5>
            <div style={{ height: 12 }} aria-hidden />
            <h5>
              <strong>Collection Date</strong>
            </h5>
            <h5>
              <Moment format="MM/DD/YYYY">
                {bloodworkAnalysis.bloodwork.collectionDate}
              </Moment>
            </h5>
            <div style={{ height: 12 }} aria-hidden />
            {this.state.previousBloodwork && (
              <>
                <h5>
                  <strong>Previous Collection Date</strong>
                </h5>
                <h5>
                  <Moment format="MM/DD/YYYY">
                    {this.state.previousBloodwork.collectionDate}
                  </Moment>
                </h5>
                <div style={{ height: 12 }} aria-hidden />
              </>
            )}
            <h5>
              <strong>Analysis Date</strong>
            </h5>
            <h5>
              <Moment format="MM/DD/YYYY">{today}</Moment>
            </h5>
          </div>

          {/* Sections */}
          <div>
            <PDFSection
              withNotes={this.state.withNotes}
              bloodworkAnalysis={bloodworkAnalysis}
              furtherConsiderations={furtherConsiderations}
              title="Red Blood Cell (RBC) Analysis"
              section={Section.RBC}
              panelMarkers={bloodworkAnalysis.getRBCPanelMarkers()}
            />
          </div>

          <div
            className={this.pageBreakClassName(
              bloodworkAnalysis.getIronPanelMarkers(),
            )}
          >
            <PDFSection
              withNotes={this.state.withNotes}
              bloodworkAnalysis={bloodworkAnalysis}
              furtherConsiderations={furtherConsiderations}
              title="Iron Analysis"
              section={Section.IRON}
              panelMarkers={bloodworkAnalysis.getIronPanelMarkers()}
            />
          </div>

          {hasWbcValue && (
            <div>
              <PDFSection
                withNotes={this.state.withNotes}
                bloodworkAnalysis={bloodworkAnalysis}
                furtherConsiderations={furtherConsiderations}
                title="Immune System Analysis"
                section={Section.WBC}
                panelMarkers={bloodworkAnalysis.getWBCPanelMarkers()}
              />
            </div>
          )}

          <div
            className={this.pageBreakClassName([
              ...bloodworkAnalysis.getPlateletPanelMarkers(),
              ...bloodworkAnalysis.getGlucosePanelMarkers(),
            ])}
          >
            <PDFSection
              withNotes={this.state.withNotes}
              bloodworkAnalysis={bloodworkAnalysis}
              furtherConsiderations={furtherConsiderations}
              title="Coagulation Analysis"
              section={Section.PLATELETS}
              panelMarkers={bloodworkAnalysis.getPlateletPanelMarkers()}
            />
            <div style={{ height: 12 }} aria-hidden />
            <PDFSection
              withNotes={this.state.withNotes}
              bloodworkAnalysis={bloodworkAnalysis}
              furtherConsiderations={furtherConsiderations}
              title="Glucose Analysis"
              section={Section.GLUCOSE}
              panelMarkers={bloodworkAnalysis.getGlucosePanelMarkers()}
            />
          </div>

          <div
            className={this.pageBreakClassName([
              ...bloodworkAnalysis.getKidneyPanelMarkers(),
              ...bloodworkAnalysis.getProteinsPanelMarkers(),
            ])}
          >
            <PDFSection
              withNotes={this.state.withNotes}
              bloodworkAnalysis={bloodworkAnalysis}
              furtherConsiderations={furtherConsiderations}
              title="Kidney Analysis"
              section={Section.KIDNEY}
              panelMarkers={bloodworkAnalysis.getKidneyPanelMarkers()}
            />
            <div style={{ height: 12 }} aria-hidden />
            <PDFSection
              withNotes={this.state.withNotes}
              bloodworkAnalysis={bloodworkAnalysis}
              furtherConsiderations={furtherConsiderations}
              title="Proteins Analysis"
              section={Section.PROTEINS}
              panelMarkers={bloodworkAnalysis.getProteinsPanelMarkers()}
            />
          </div>

          <div
            className={this.pageBreakClassName([
              ...bloodworkAnalysis.getMineralPanelMarkers(),
            ])}
          >
            <PDFSection
              withNotes={this.state.withNotes}
              bloodworkAnalysis={bloodworkAnalysis}
              furtherConsiderations={furtherConsiderations}
              title="Nutrients Analysis"
              section={Section.NUTRIENTS}
              panelMarkers={bloodworkAnalysis.getMineralPanelMarkers()}
            />
          </div>

          <div
            className={this.pageBreakClassName([
              ...bloodworkAnalysis.getElectrolytePanelMarkers(),
            ])}
          >
            <PDFSection
              withNotes={this.state.withNotes}
              bloodworkAnalysis={bloodworkAnalysis}
              furtherConsiderations={furtherConsiderations}
              title="Electrolytes Analysis"
              section={Section.ELECTROLYTES}
              panelMarkers={bloodworkAnalysis.getElectrolytePanelMarkers()}
            />
          </div>

          <div
            className={this.pageBreakClassName(
              bloodworkAnalysis.getLipidsPanelMarkers(),
            )}
          >
            <br />
            <PDFSection
              withNotes={this.state.withNotes}
              bloodworkAnalysis={bloodworkAnalysis}
              furtherConsiderations={furtherConsiderations}
              title="Lipids Analysis"
              section={Section.LIPIDS}
              panelMarkers={bloodworkAnalysis.getLipidsPanelMarkers()}
            />
          </div>

          <div
            className={this.pageBreakClassName(
              bloodworkAnalysis.getInflammationOxidativeStressPanelMarkers(),
            )}
          >
            <br />
            <PDFSection
              withNotes={this.state.withNotes}
              bloodworkAnalysis={bloodworkAnalysis}
              furtherConsiderations={furtherConsiderations}
              title="Inflammation/Oxidative Stress Analysis"
              section={Section.INFLAMMATION_OXIDATIVE_STRESS}
              panelMarkers={bloodworkAnalysis.getInflammationOxidativeStressPanelMarkers()}
            />
          </div>

          <div
            className={this.pageBreakClassName(
              bloodworkAnalysis.getThyroidPanelMarkers(),
            )}
          >
            <br />
            <PDFSection
              withNotes={this.state.withNotes}
              bloodworkAnalysis={bloodworkAnalysis}
              furtherConsiderations={furtherConsiderations}
              title="Thyroid Analysis"
              section={Section.THYROID}
              panelMarkers={bloodworkAnalysis.getThyroidPanelMarkers()}
            />
          </div>

          <div
            className={this.pageBreakClassName(
              bloodworkAnalysis.getSexHormonePanelMarkers(),
            )}
          >
            <br />
            <PDFSection
              withNotes={this.state.withNotes}
              bloodworkAnalysis={bloodworkAnalysis}
              furtherConsiderations={furtherConsiderations}
              title="Hormones Analysis"
              section={Section.SEX_HORMONE}
              panelMarkers={bloodworkAnalysis.getSexHormonePanelMarkers()}
            />
          </div>

          {/* Global Notes (trimmed top whitespace) */}
          <div>
            {this.state.withNotes && globalNotesText && (
              <Card
                className="disclaimer-panel mt-3 mb-5"
                style={{ whiteSpace: 'pre-line' }}
              >
                <Card.Header className="py-1">Notes</Card.Header>
                <Card.Body
                  className="disclaimer-body"
                  style={{ whiteSpace: 'pre-line', paddingTop: 0 }}
                >
                  {globalNotesText}
                </Card.Body>
              </Card>
            )}
          </div>

          {/* Disclaimer (stays last) */}
          <DisclaimerSection furtherConsiderations={furtherConsiderations} />
        </div>
      </div>
    );
  }
}
